import { TemplateRef } from '@angular/core';
import { ObjectTools } from '../../../tools/object-tools';
import { SolTooltipEntry } from '../../../services/tooltip.service';
import { SolTooltipPosition } from '../../../models/core/sol-tooltip';
import { SolText } from '../../../models/core/sol-text';

export interface SolTooltipOverlay {
  readonly id: string;
  readonly text: SolText;
  readonly left: number;
  readonly top: number;
  readonly transform: string;
  readonly template: TemplateRef<unknown> | undefined;
  readonly custom: boolean;
  readonly wide: boolean;
}

export class SolTooltipsState {
  private static readonly GAP = 8;
  private static readonly VIEWPORT_MARGIN = 8;

  private constructor(
    public readonly tooltips: SolTooltipEntry[],
    public readonly overlays: SolTooltipOverlay[],
    public readonly visible: boolean,
  ) {}

  public static of(): SolTooltipsState {
    return new SolTooltipsState([], [], false);
  }

  public withTooltips(tooltips: SolTooltipEntry[]): SolTooltipsState {
    return this.copy({ tooltips }).recalculateOverlays();
  }

  public withViewportClamp(id: string, rect: DOMRect, clientWidth: number, clientHeight: number): SolTooltipsState {
    const entry = this.tooltips.find(t => t.id === id);
    if (!entry) {
      return this;
    }

    const position = SolTooltipsState.toFittingPosition(entry, rect, clientWidth, clientHeight);
    const overlay = SolTooltipsState.toOverlay({ ...entry, position });
    const clamped = SolTooltipsState.clamp(overlay, position, rect, clientWidth, clientHeight);
    const overlays = this.overlays.map(o => (o.id === id ? clamped : o));
    return this.copy({ overlays });
  }

  private recalculateOverlays(): SolTooltipsState {
    const visible = this.tooltips.length > 0;
    const overlays = this.tooltips.map(entry => SolTooltipsState.toOverlay(entry));
    return this.copy({ overlays, visible });
  }

  private static toOverlay(entry: SolTooltipEntry): SolTooltipOverlay {
    const gap = SolTooltipsState.GAP;
    const bounds = entry.triggerBounds;
    const pos = entry.position;

    let left: number;
    let top: number;
    let transform: string;

    switch (pos) {
      case 'bottom':
        left = bounds.left + bounds.width / 2;
        top = bounds.bottom + gap;
        transform = 'translateX(-50%)';
        break;
      case 'top':
        left = bounds.left + bounds.width / 2;
        top = bounds.top - gap;
        transform = 'translate(-50%, -100%)';
        break;
      case 'left':
        left = bounds.left - gap;
        top = bounds.top + bounds.height / 2;
        transform = 'translate(-100%, -50%)';
        break;
      case 'right':
        left = bounds.right + gap;
        top = bounds.top + bounds.height / 2;
        transform = 'translateY(-50%)';
        break;
    }

    return {
      id: entry.id,
      text: entry.text,
      left,
      top,
      transform,
      template: entry.template,
      custom: entry.custom ?? false,
      wide: entry.wide ?? false,
    };
  }

  private static toFittingPosition(
    entry: SolTooltipEntry,
    rect: DOMRect,
    clientWidth: number,
    clientHeight: number,
  ): SolTooltipPosition {
    const space = SolTooltipsState.toSpace(entry.triggerBounds, clientWidth, clientHeight);
    const position = entry.position;
    const flipped = SolTooltipsState.flipPosition(position);
    const size = SolTooltipsState.isVertical(position) ? rect.height : rect.width;
    if (size <= space[position] || space[flipped] <= space[position]) {
      return position;
    }
    return flipped;
  }

  private static toSpace(
    bounds: DOMRect,
    clientWidth: number,
    clientHeight: number,
  ): Record<SolTooltipPosition, number> {
    const reserve = SolTooltipsState.GAP + SolTooltipsState.VIEWPORT_MARGIN;
    return {
      top: bounds.top - reserve,
      bottom: clientHeight - bounds.bottom - reserve,
      left: bounds.left - reserve,
      right: clientWidth - bounds.right - reserve,
    };
  }

  private static flipPosition(pos: SolTooltipPosition): SolTooltipPosition {
    switch (pos) {
      case 'top':
        return 'bottom';
      case 'bottom':
        return 'top';
      case 'left':
        return 'right';
      case 'right':
        return 'left';
    }
  }

  private static isVertical(pos: SolTooltipPosition): boolean {
    return pos === 'top' || pos === 'bottom';
  }

  private static clamp(
    overlay: SolTooltipOverlay,
    position: SolTooltipPosition,
    rect: DOMRect,
    clientWidth: number,
    clientHeight: number,
  ): SolTooltipOverlay {
    if (SolTooltipsState.isVertical(position)) {
      const left = SolTooltipsState.toClampedStart(overlay.left - rect.width / 2, rect.width, clientWidth);
      return { ...overlay, left: left + rect.width / 2 };
    }
    const top = SolTooltipsState.toClampedStart(overlay.top - rect.height / 2, rect.height, clientHeight);
    return { ...overlay, top: top + rect.height / 2 };
  }

  private static toClampedStart(start: number, size: number, clientSize: number): number {
    const margin = SolTooltipsState.VIEWPORT_MARGIN;
    return Math.max(margin, Math.min(start, clientSize - margin - size));
  }

  private copy(props: Partial<SolTooltipsState>): SolTooltipsState {
    return ObjectTools.copy<SolTooltipsState>(this, props);
  }
}
